import { create } from 'zustand'
import {
  ActivationType,
  CreateActivationTypeRequest,
  UpdateActivationTypeRequest
} from '../types/activation'
import { activationTypeService } from '../services/activationTypeService'

interface ActivationTypeState {
  // State
  activationTypes: ActivationType[]
  loading: boolean
  error: string | null

  // Actions
  loadActivationTypes: (customerId: string, activeOnly?: boolean) => Promise<void>
  createActivationType: (
    customerId: string,
    data: CreateActivationTypeRequest
  ) => Promise<ActivationType>
  updateActivationType: (
    id: string,
    customerId: string,
    data: UpdateActivationTypeRequest
  ) => Promise<ActivationType>
  deleteActivationType: (id: string, customerId: string) => Promise<void>
  reset: () => void
}

const useActivationTypeStore = create<ActivationTypeState>((set) => ({
  // Initial State
  activationTypes: [],
  loading: false,
  error: null,

  // Actions
  loadActivationTypes: async (customerId: string, activeOnly?: boolean) => {
    set({ loading: true, error: null })
    try {
      const activationTypes = await activationTypeService.getActivationTypes(
        customerId,
        activeOnly
      )
      set({ activationTypes, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load activation types',
        loading: false
      })
    }
  },

  createActivationType: async (customerId: string, data: CreateActivationTypeRequest) => {
    const activationType = await activationTypeService.createActivationType(customerId, data)
    return activationType
  },

  updateActivationType: async (
    id: string,
    customerId: string,
    data: UpdateActivationTypeRequest
  ) => {
    const activationType = await activationTypeService.updateActivationType(id, customerId, data)
    return activationType
  },

  deleteActivationType: async (id: string, customerId: string) => {
    await activationTypeService.deleteActivationType(id, customerId)

    // Remove from local state - safe for delete since no nested objects needed
    set((state) => ({
      activationTypes: state.activationTypes.filter((at) => at.id !== id)
    }))
  },

  reset: () =>
    set({
      activationTypes: [],
      loading: false,
      error: null
    })
}))

export default useActivationTypeStore
